import { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  useTheme,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  alpha,
  Container,
  Fade,
} from "@mui/material";
import MenuDashboard, { Paginas } from "../../../components/MenuDashboard";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import RoomIcon from "@mui/icons-material/Room";
import PersonIcon from "@mui/icons-material/Person";
import WarningIcon from "@mui/icons-material/Warning";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import useStyles from "./styles";
import DadosPessoais from "./dadosPessoais";
import MeusEnderecos from "./endereco";
import Documentos from "./documentos";
import MeusDadosBancarios from "./MeusDadosBancarios";
import MeusCartoesCredito from "./MeusCartoesCredito";
import MinhaConta from "./MinhaConta";
import BadgeCustom from "../../../components/Badges/BadgeCustom";
import useCheckPendencia from "../../../hooks/useCheckPendencia";
import Pendencias from "./pendencias";
import useAlert from "../../../hooks/useAlert";
import TipoPanels from "../../../util/typesTipoPanelsMeusDados";

const painelsMenus = [
  {
    title: "Dados Pessoais",
    text: "Mantenha os dados pessoais atualizados.",
    icon: <PersonIcon />,
    idTipo: TipoPanels.editDados,
    color: "#1976d2",
  },
  {
    title: "Endereço",
    text: "Mantenha o endereço atualizado para não haver conflito em suas operações.",
    icon: <RoomIcon />,
    idTipo: TipoPanels.editEnderecos,
    color: "#2e7d32",
  },
  {
    title: "Documentos",
    text: "Confira os documentos de seu cadastro ou insira novos documentos.",
    icon: <InsertDriveFileIcon />,
    idTipo: TipoPanels.editDocumentos,
    color: "#ed6c02",
  },
  {
    title: "Dados bancários",
    text: "Confira os dados bancários de seu cadastro ou insira novos dados.",
    icon: <AccountBalanceIcon />,
    idTipo: TipoPanels.editDadosBancarios,
    color: "#9c27b0",
  },
  {
    title: "Pendências",
    text: "Verifica se possui alguma pendência na sua conta.",
    icon: <WarningIcon />,
    idTipo: TipoPanels.pendencias,
    color: "#d32f2f",
  },
  {
    title: "Minha Conta",
    text: "Configurar sua conta na loja.",
    icon: <ManageAccountsIcon />,
    idTipo: TipoPanels.editMinhaConta,
    color: "#0288d1",
  },
  {
    title: "Meus Cartoes de Crédito",
    text: "Ative e desative cartões que poderão ser usados.",
    icon: <CreditCardIcon />,
    idTipo: TipoPanels.editMeusCartoesCredito,
    color: "#f50a80ff",
  },
];

export const MeusDados = () => {
  const cssStyles = useStyles();
  const theme = useTheme();
  const [panel, setPanel] = useState(TipoPanels.OptionsEditions);
  const { hasPendencia, messageError } = useCheckPendencia();
  const { showAlertError } = useAlert();

  useEffect(() => {
    if (!!messageError) showAlertError(messageError);
  }, [messageError]);

  const hendlerChangePanel = (id) => (event) => {
    setPanel(id);
  };

  const MenuCard = ({ item, index }) => {
    const hasBadge = item.idTipo === TipoPanels.pendencias && hasPendencia;

    return (
      <Grid item xs={12} sm={6} md={4} key={index}>
        <Fade in timeout={500 + index * 100}>
          <Card
            elevation={2}
            sx={{
              height: "100%",
              borderRadius: 3,
              border: `1px solid ${alpha(item.color, 0.2)}`,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: `0 8px 24px ${alpha(item.color, 0.2)}`,
                borderColor: item.color,
              },
            }}
          >
            <CardActionArea
              onClick={hendlerChangePanel(item.idTipo)}
              sx={{ height: "100%", p: 3 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  height: "100%",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: 50,
                      height: 50,
                      borderRadius: "12px",
                      bgcolor: alpha(item.color, 0.1),
                      color: item.color,
                      mr: 2,
                    }}
                  >
                    {item.icon}
                  </Box>

                  {hasBadge && (
                    <BadgeCustom
                      isImportant={true}
                      quantidade={"!"}
                      sx={{
                        ml: "auto",
                      }}
                    />
                  )}
                </Box>

                <CardContent sx={{ p: 0, flex: 1 }}>
                  <Typography
                    variant="h6"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      color: theme.palette.text.primary,
                      mb: 1,
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      lineHeight: 1.5,
                    }}
                  >
                    {item.text}
                  </Typography>
                </CardContent>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mt: "auto",
                  }}
                >
                  <Chip
                    label="Acessar"
                    size="small"
                    sx={{
                      bgcolor: alpha(item.color, 0.1),
                      color: item.color,
                      fontWeight: 500,
                    }}
                  />
                  <ArrowForwardIcon
                    sx={{
                      color: item.color,
                      fontSize: 18,
                    }}
                  />
                </Box>
              </Box>
            </CardActionArea>
          </Card>
        </Fade>
      </Grid>
    );
  };

  return (
    <MenuDashboard pagina={Paginas.MeusDados}>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        {panel === TipoPanels.OptionsEditions && (
          <>
            <Box sx={{ textAlign: "center", mb: 6 }}>
              <Typography
                variant="h4"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  color: theme.palette.primary.main,
                  mb: 1,
                }}
              >
                Meus Dados
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{ maxWidth: 600, mx: "auto" }}
              >
                Gerencie todas as informações da sua conta em um único lugar
              </Typography>
            </Box>

            <Grid container spacing={3}>
              {painelsMenus.map((item, index) => (
                <MenuCard key={index} item={item} index={index} />
              ))}
            </Grid>
          </>
        )}

        {/* Painéis de conteúdo */}
        <Box sx={{ mt: 4 }}>
          {panel === TipoPanels.editDados && (
            <DadosPessoais
              handlerCancelEdit={hendlerChangePanel(TipoPanels.OptionsEditions)}
            />
          )}
          {panel === TipoPanels.editEnderecos && (
            <MeusEnderecos
              handlerCancelEdit={hendlerChangePanel(TipoPanels.OptionsEditions)}
            />
          )}
          {panel === TipoPanels.editDocumentos && (
            <Documentos
              handlerCancelEdit={hendlerChangePanel(TipoPanels.OptionsEditions)}
            />
          )}
          {panel === TipoPanels.editDadosBancarios && (
            <MeusDadosBancarios
              handlerCancelEdit={hendlerChangePanel(TipoPanels.OptionsEditions)}
            />
          )}

          {panel === TipoPanels.editMeusCartoesCredito && (
            <MeusCartoesCredito
              handlerCancelEdit={hendlerChangePanel(TipoPanels.OptionsEditions)}
            />
          )}
          {panel === TipoPanels.pendencias && (
            <Pendencias
              handlerCancelEdit={hendlerChangePanel(TipoPanels.OptionsEditions)}
              setPanel={setPanel}
            />
          )}
          {panel === TipoPanels.editMinhaConta && (
            <MinhaConta
              handlerCancelEdit={hendlerChangePanel(TipoPanels.OptionsEditions)}
            />
          )}
        </Box>
      </Container>
    </MenuDashboard>
  );
};
